import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import {
  Alert,
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import RemoveIcon from "@mui/icons-material/Remove";
import { setSnackbar } from "../../snackbar/api/snackbar";
import { changeOwnPassword, getActiveUsers } from "../api/actions";
import { EVERYONE_CAN, ROLES, flag, fullName, hasRole, isServiceAccount } from "../accessRoles";

const EMPTY_PW = { oldPassword: "", password: "", verify: "" };

// Opened from the avatar menu. Everyone can see their own access and change their password.
function MyAccountDrawer({ open, onClose, user, activeUsers, getActiveUsers, changeOwnPassword, setSnackbar }) {
  const me = user.details || {};
  const [pw, setPw] = useState(EMPTY_PW);
  const [pwError, setPwError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setPw(EMPTY_PW);
      setPwError(null);
      if (activeUsers.length === 0) getActiveUsers(false);
    }
  }, [open]);

  const superAdmins = activeUsers
    .filter((u) => flag(u.isActive) && flag(u.isSuperUser) && !isServiceAccount(u) && u.uuid !== me.uuid)
    .map(fullName);

  const submit = async (e) => {
    e.preventDefault();
    if (!pw.oldPassword || !pw.password || !pw.verify) return setPwError("Fill in all three fields.");
    if (pw.password !== pw.verify) return setPwError("The new passwords don't match.");
    if (pw.password === pw.oldPassword) return setPwError("Pick a password you aren't already using.");
    setSaving(true);
    setPwError(null);
    try {
      await changeOwnPassword({ uuid: me.uuid, oldPassword: pw.oldPassword, password: pw.password });
      setPw(EMPTY_PW);
      setSnackbar(true, "success", "Password updated");
    } catch (err) {
      setPwError("That didn't work. Check your current password and try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: { xs: "100%", sm: 420 } } }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2.5, py: 1.5 }}>
        <Typography variant="h6">My account</Typography>
        <IconButton onClick={onClose} aria-label="Close"><CloseIcon /></IconButton>
      </Box>
      <Divider />
      <Box sx={{ p: 2.5, display: "grid", gap: 3, overflowY: "auto" }}>
        <Box sx={{ display: "grid", gridTemplateColumns: "80px minmax(0,1fr)", rowGap: 0.75, columnGap: 1.5 }}>
          <Typography variant="body2" color="text.secondary">Name</Typography>
          <Typography variant="body2">{fullName(me)}</Typography>
          <Typography variant="body2" color="text.secondary">Email</Typography>
          <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>{me.email}</Typography>
          {me.phone && (
            <>
              <Typography variant="body2" color="text.secondary">Phone</Typography>
              <Typography variant="body2">{me.phone}</Typography>
            </>
          )}
        </Box>

        <Box sx={{ display: "grid", gap: 1.25 }}>
          <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.5 }}>Your access</Typography>
          {ROLES.map((role) => {
            const yes = hasRole(me, role.key);
            return (
              <Box key={role.key} sx={{ display: "grid", gridTemplateColumns: "24px minmax(0,1fr)", gap: 1 }}>
                {yes ? <CheckIcon fontSize="small" color="success" /> : <RemoveIcon fontSize="small" color="disabled" />}
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500, color: yes ? "text.primary" : "text.secondary" }}>{role.name}</Typography>
                  <Typography variant="caption" color="text.secondary">{role.opens}</Typography>
                </Box>
              </Box>
            );
          })}
          <Typography variant="caption" color="text.secondary">Everyone: {EVERYONE_CAN}</Typography>
          <Typography variant="body2" color="text.secondary">
            {superAdmins.length > 0
              ? `Need more access? Ask a super admin: ${superAdmins.join(", ")}.`
              : "Need more access? Ask a super admin."}
          </Typography>
        </Box>

        <Box component="form" onSubmit={submit} noValidate sx={{ display: "grid", gap: 1.5 }}>
          <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.5 }}>Change password</Typography>
          <TextField size="small" type="password" label="Current password" autoComplete="current-password"
            value={pw.oldPassword} onChange={(e) => setPw({ ...pw, oldPassword: e.target.value })} />
          <TextField size="small" type="password" label="New password" autoComplete="new-password"
            value={pw.password} onChange={(e) => setPw({ ...pw, password: e.target.value })} />
          <TextField size="small" type="password" label="Type it again" autoComplete="new-password"
            value={pw.verify} onChange={(e) => setPw({ ...pw, verify: e.target.value })} />
          {pwError && <Alert severity="error">{pwError}</Alert>}
          <Box>
            <Button type="submit" variant="contained" color="secondary" disabled={saving} sx={{ textTransform: "none" }}>
              {saving ? "Updating…" : "Update password"}
            </Button>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
}

function mapStateToProps(state) {
  return {
    user: state.user,
    activeUsers: state.setting?.activeUsers?.items || [],
  };
}

export default connect(mapStateToProps, { getActiveUsers, changeOwnPassword, setSnackbar })(MyAccountDrawer);
